import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { customerApi } from '../api/customers';
import { isApiError, refusalMessage } from '../api/errors';
import { riderApi } from '../api/riders';
import DriverPasswordField from '../components/driver/DriverPasswordField';
import { clearCustomerSession } from '../lib/customerSession';
import { clearDriverSession, isDriverSignedIn } from '../lib/driverSession';
import './driverLoginPremium.css';

function IconEnvelope() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M4 6h16v12H4V6Zm0 0 8 7 8-7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * "Delete my account", a client's or a rider's (InGo ADR 0038; Google Play
 * requires it in the app). The server deletes the account thirty days after
 * the request, ends every session at once, and emails a link that keeps it:
 * choosing a new password there. Confirmed by the password, or by the
 * account's own address for an account made with Google, whose password
 * nobody knows.
 *
 * The client's page is behind `CustomerAuthGate`. The rider's is not behind
 * the approval gate: a rider still waiting for approval may delete too.
 *
 * @param {{ realm?: 'customer' | 'driver' }} props
 */
export default function DeleteAccountPage({ realm = 'customer', customers = customerApi, riders = riderApi }) {
  const navigate = useNavigate();
  const isRider = realm === 'driver';
  const [byEmail, setByEmail] = useState(false);
  const [value, setValue] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState(false);

  if (isRider && !isDriverSignedIn()) {
    return <Navigate to="/driver/login" replace />;
  }

  const switchWay = () => {
    setByEmail((b) => !b);
    setValue('');
    setFieldError('');
  };

  const submit = async (e) => {
    e.preventDefault();
    setFieldError('');
    setProblem('');
    if (!value.trim()) {
      setFieldError(byEmail ? 'Type your email address.' : 'Enter your password.');
      return;
    }
    setBusy(true);
    try {
      const realmApi = isRider ? riders : customers;
      const { message } = await realmApi.requestDeletion(byEmail ? { email: value.trim() } : { password: value });
      // One token served both realms, and the server has ended it: neither profile stays.
      clearDriverSession();
      clearCustomerSession();
      navigate(isRider ? '/driver/login' : '/login', { replace: true, state: { deletionScheduled: message } });
    } catch (error) {
      if (isApiError(error) && error.code === 'ACCOUNT_TIED') {
        // The whole sentence: the field message alone is only the first tie, without "not yet".
        setProblem(error.message);
      } else if (isApiError(error) && error.errors?.confirmation?.[0]) {
        setFieldError(error.errors.confirmation[0]);
      } else {
        setProblem(refusalMessage(error));
      }
      setBusy(false);
    }
  };

  return (
    <div className="dp-login-page">
      <main className="dp-login-body dp-login-body--alone">
        <div className="dp-login-card">
          <form className="dp-login-form" onSubmit={submit} noValidate>
            <h1 className="dp-login-title">Delete my account</h1>
            <p className="dp-login-subtitle">
              Your account is deleted 30 days after you ask. You are signed out on every phone now, and we email you a link: choosing a new
              password from it keeps your account.
            </p>
            <p className="dp-login-subtitle">
              After 30 days your name and contact details are erased. Your past deliveries stay in InGo&apos;s records, without them.
              {isRider ? ' Commission you owe InGo on cash deliveries is still owed.' : null}
            </p>

            {problem ? (
              <p className="dp-login-error" role="alert">
                {problem}
              </p>
            ) : null}

            {byEmail ? (
              <div className="dp-login-field">
                <label className="dp-login-label" htmlFor="da-email">
                  Your email address
                </label>
                <div className="dp-login-input-wrap">
                  <span className="dp-login-iconbox" aria-hidden>
                    <IconEnvelope />
                  </span>
                  <input
                    className="dp-login-input"
                    id="da-email"
                    name="email"
                    type="email"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    autoComplete="email"
                    placeholder="you@example.com"
                    disabled={busy}
                    aria-invalid={fieldError ? true : undefined}
                    aria-describedby={fieldError ? 'da-email-error' : undefined}
                  />
                </div>
                {fieldError ? (
                  <p className="dp-login-error" id="da-email-error" role="alert">
                    {fieldError}
                  </p>
                ) : null}
              </div>
            ) : (
              <DriverPasswordField
                id="da-password"
                label="Your password"
                value={value}
                onChange={setValue}
                autoComplete="current-password"
                error={fieldError}
                disabled={busy}
              />
            )}

            <p className="dp-login-customer-line" style={{ margin: '0.25rem auto 1.25rem' }}>
              <button type="button" className="dp-login-customer-link dp-login-link-btn" onClick={switchWay} disabled={busy}>
                {byEmail ? 'Use my password instead' : 'I sign in with Google'}
              </button>
            </p>

            <button type="submit" className="dp-login-btn dp-login-btn--danger" disabled={busy}>
              {busy ? 'Asking…' : 'Delete my account'}
            </button>
          </form>

          <p className="dp-login-customer-line">
            <button
              type="button"
              className="dp-login-customer-link dp-login-link-btn"
              onClick={() => navigate(isRider ? '/driver/profile' : '/profile', { replace: true })}
              disabled={busy}
            >
              Keep my account
            </button>
          </p>
        </div>
      </main>
    </div>
  );
}
